/**
 * Currency Service
 *
 * Singleton service for managing gold currency, Score Boost upgrade,
 * and Combo Shield consumable items. Persisted state lives in PlayerStore
 * (single localStorage key, checksum-validated). Session-only state
 * (active shields, score-boost session) lives in-memory and resets per game.
 */

import type { PlayerStore } from '@/state/playerStore';
import {
  type CurrencyState,
  type GoldAdResult,
  type GoldEarnedResult,
  INITIAL_SCORE_BOOST_SESSION,
  INITIAL_SHIELD_SESSION,
  type PurchaseResult,
  type ScoreBoostSessionState,
  type ShieldSessionState,
} from './types';
import {
  COMBO_SHIELD,
  GOLD_CONSTANTS,
  SCORE_BOOST,
  calculateGameGold,
  getScoreBoostMultiplier,
} from './upgrades';

export interface CurrencyService {
  subscribe(listener: (state: CurrencyState) => void): () => void;
  getCurrencyState(): CurrencyState;
  getShieldSession(): ShieldSessionState;
  getScoreBoostSession(): ScoreBoostSessionState;
  getGold(): number;
  getScoreBoostLevel(): number;
  getComboShields(): number;
  getScoreBoostMultiplier(): number;
  calculateBoostedScore(rawScore: number): number;
  canWatchGoldAd(): boolean;
  getRemainingGoldAdCooldown(): number;
  watchGoldAd(): GoldAdResult;
  calculateGoldFromGame(score: number, maxCombo: number, isPersonalBest: boolean): GoldEarnedResult;
  awardGameGold(goldResult: GoldEarnedResult): void;
  getScoreBoostUpgradeCost(): number;
  purchaseScoreBoost(): PurchaseResult;
  canPurchaseShields(amount: number): boolean;
  purchaseShields(bulk?: boolean): PurchaseResult;
  activateShields(count: number): number;
  getShieldsRemaining(): number;
  getShieldsActive(): number;
  useShield(): boolean;
  endGameSession(): void;
  startGameSession(): void;
  resetCurrencyState(): void;
}

export function createCurrencyService(store: PlayerStore): CurrencyService {
  let shieldSession: ShieldSessionState = { ...INITIAL_SHIELD_SESSION };
  let scoreBoostSession: ScoreBoostSessionState = { ...INITIAL_SCORE_BOOST_SESSION };
  const listeners = new Set<(state: CurrencyState) => void>();

  store.subscribe((save) => {
    for (const listener of listeners) listener({ ...save.currency });
  });

  const read = (): CurrencyState => store.get().currency;

  const writeCurrency = (next: CurrencyState): void => {
    store.setCurrency(next);
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    getCurrencyState() {
      return { ...read() };
    },
    getShieldSession() {
      return { ...shieldSession };
    },
    getScoreBoostSession() {
      return { ...scoreBoostSession };
    },
    getGold() {
      return read().gold;
    },
    getScoreBoostLevel() {
      return read().scoreBoostLevel;
    },
    getComboShields() {
      return read().comboShields;
    },

    getScoreBoostMultiplier() {
      return getScoreBoostMultiplier(read().scoreBoostLevel);
    },

    calculateBoostedScore(rawScore) {
      const multiplier = getScoreBoostMultiplier(read().scoreBoostLevel);
      const level = read().scoreBoostLevel;
      const boostedScore = Math.round(rawScore * (1 + multiplier));

      if (scoreBoostSession.rawScore === 0 && scoreBoostSession.boostedScore === 0) {
        scoreBoostSession = {
          ...scoreBoostSession,
          isActive: multiplier > 0,
          level,
          multiplier,
        };
      }

      scoreBoostSession = {
        ...scoreBoostSession,
        rawScore: scoreBoostSession.rawScore + rawScore,
        boostedScore: scoreBoostSession.boostedScore + boostedScore,
      };

      return boostedScore;
    },

    canWatchGoldAd() {
      const cur = read();
      const now = Date.now();
      const elapsed = now - cur.lastGoldAdWatchTime;
      const cooldownMs = GOLD_CONSTANTS.AD_COOLDOWN_MINUTES * 60 * 1000;
      return elapsed >= cooldownMs;
    },

    getRemainingGoldAdCooldown() {
      const cur = read();
      const cooldownMs = GOLD_CONSTANTS.AD_COOLDOWN_MINUTES * 60 * 1000;
      const remaining = cooldownMs - (Date.now() - cur.lastGoldAdWatchTime);
      if (remaining <= 0) return 0;
      if (remaining > cooldownMs * 10) return 0;
      return Math.ceil(remaining / (60 * 1000));
    },

    watchGoldAd() {
      if (!this.canWatchGoldAd()) {
        return {
          success: false,
          goldEarned: 0,
          error: `Please wait ${this.getRemainingGoldAdCooldown()} minutes before watching another ad.`,
        };
      }
      const cur = read();
      const goldEarned = GOLD_CONSTANTS.GOLD_PER_AD;
      writeCurrency({
        ...cur,
        gold: cur.gold + goldEarned,
        totalGoldEarned: cur.totalGoldEarned + goldEarned,
        lastGoldAdWatchTime: Date.now(),
      });
      return { success: true, goldEarned };
    },

    calculateGoldFromGame(score, maxCombo, isPersonalBest) {
      return calculateGameGold(score, maxCombo, isPersonalBest);
    },

    awardGameGold(goldResult) {
      const cur = read();
      writeCurrency({
        ...cur,
        gold: cur.gold + goldResult.totalGold,
        totalGoldEarned: cur.totalGoldEarned + goldResult.totalGold,
      });
    },

    getScoreBoostUpgradeCost() {
      const lvl = read().scoreBoostLevel;
      if (lvl >= SCORE_BOOST.maxLevel) return -1;
      return SCORE_BOOST.costs[lvl] as number;
    },

    purchaseScoreBoost() {
      const cur = read();
      const lvl = cur.scoreBoostLevel;
      if (lvl >= SCORE_BOOST.maxLevel) {
        return {
          success: false,
          newLevel: lvl,
          goldSpent: 0,
          error: 'Already at maximum level',
        };
      }
      const cost = SCORE_BOOST.costs[lvl] as number;
      if (cur.gold < cost) {
        return {
          success: false,
          newLevel: lvl,
          goldSpent: 0,
          error: 'Not enough gold',
        };
      }
      writeCurrency({
        ...cur,
        gold: cur.gold - cost,
        totalGoldSpent: cur.totalGoldSpent + cost,
        scoreBoostLevel: lvl + 1,
        upgradesPurchased: cur.upgradesPurchased + 1,
      });
      return { success: true, newLevel: lvl + 1, goldSpent: cost };
    },

    canPurchaseShields(amount) {
      return read().comboShields + amount <= COMBO_SHIELD.maxShields;
    },

    purchaseShields(bulk = false) {
      const amount = bulk ? COMBO_SHIELD.bulkAmount : 1;
      const cost = bulk ? COMBO_SHIELD.bulkCost : COMBO_SHIELD.costPerShield;
      const cur = read();
      if (cur.comboShields + amount > COMBO_SHIELD.maxShields) {
        return {
          success: false,
          newCount: cur.comboShields,
          goldSpent: 0,
          error: `Cannot hold more than ${COMBO_SHIELD.maxShields} shields`,
        };
      }
      if (cur.gold < cost) {
        return {
          success: false,
          newCount: cur.comboShields,
          goldSpent: 0,
          error: 'Not enough gold',
        };
      }
      writeCurrency({
        ...cur,
        gold: cur.gold - cost,
        totalGoldSpent: cur.totalGoldSpent + cost,
        comboShields: cur.comboShields + amount,
      });
      return { success: true, newCount: cur.comboShields + amount, goldSpent: cost };
    },

    activateShields(count) {
      const cur = read();
      const maxActivate = Math.min(count, cur.comboShields, COMBO_SHIELD.maxPerGame);
      if (maxActivate <= 0) {
        shieldSession = { ...INITIAL_SHIELD_SESSION };
        return 0;
      }
      writeCurrency({
        ...cur,
        comboShields: cur.comboShields - maxActivate,
      });
      shieldSession = {
        shieldsActive: maxActivate,
        shieldsRemaining: maxActivate,
      };
      return maxActivate;
    },

    getShieldsRemaining() {
      return shieldSession.shieldsRemaining;
    },

    getShieldsActive() {
      return shieldSession.shieldsActive;
    },

    useShield() {
      if (shieldSession.shieldsRemaining <= 0) return false;
      shieldSession = {
        ...shieldSession,
        shieldsRemaining: shieldSession.shieldsRemaining - 1,
      };
      const cur = read();
      writeCurrency({ ...cur, shieldsUsedTotal: cur.shieldsUsedTotal + 1 });
      return true;
    },

    endGameSession() {
      if (shieldSession.shieldsRemaining > 0) {
        const cur = read();
        writeCurrency({ ...cur, comboShields: cur.comboShields + shieldSession.shieldsRemaining });
      }
      shieldSession = { ...INITIAL_SHIELD_SESSION };
    },

    startGameSession() {
      shieldSession = { ...INITIAL_SHIELD_SESSION };
      const cur = read();
      const activate = Math.min(cur.comboShields, COMBO_SHIELD.maxPerGame);
      if (activate > 0) {
        writeCurrency({ ...cur, comboShields: cur.comboShields - activate });
        shieldSession = { shieldsActive: activate, shieldsRemaining: activate };
      }
      const lvl = read().scoreBoostLevel;
      const multiplier = getScoreBoostMultiplier(lvl);
      scoreBoostSession = {
        isActive: multiplier > 0,
        level: lvl,
        multiplier,
        rawScore: 0,
        boostedScore: 0,
      };
    },

    resetCurrencyState() {
      writeCurrency({
        gold: 0,
        lastGoldAdWatchTime: 0,
        totalGoldEarned: 0,
        totalGoldSpent: 0,
        scoreBoostLevel: 0,
        comboShields: 0,
        shieldsUsedTotal: 0,
        upgradesPurchased: 0,
      });
      shieldSession = { ...INITIAL_SHIELD_SESSION };
      scoreBoostSession = { ...INITIAL_SCORE_BOOST_SESSION };
    },
  };
}
